import {
  MinimalProjectMetadata,
  MinimalWorkspace,
  ProjectMetadata,
  ProjectMetadataIndexResponse,
  ProjectMetadataResponse,
  Workspace,
} from 'app_api/projects/types';
import {SavedReportsLimitResponse} from 'app_api/me/types';
import {EVENTS, ProjectType} from 'common/report/constants';
import {getOwnAttr} from 'common/util';
import {BILLING_DATA_GROUP_ID} from 'common/widgets/query-builder/__test__/fixtures';
import type {Results as GetFeaturesResults} from 'app_api/projects/features/types';
import {SuccessResponse} from 'common/util/api-client';

// /api/app/me/
export const API_USER_DATA = {
  status: `ok`,
  results: {
    user_id: 1,
    user_email: `dev.test@mixpanel.com`,
    user_name: `Mr. Iron`,
    projects: {
      42: {
        name: `Pro Kitten Trader`,
        permissions: [`write_customevents`],
        has_integrated: true,
        role: {
          name: `admin`,
        },
      },
    },
    organizations: {},
    workspaces: {},
  },
};

// /api/app/me/saved-reports-limit?project_id=xx
export const UNLIMITED_SAVED_REPORTS_LIMIT_RESPONSE: SavedReportsLimitResponse = {
  status: `ok`,
  results: {
    has_finite_limit: false,
    is_below_limit: true,
  },
};

export const MINIMAL_PROJECT_METADATA: MinimalProjectMetadata = {
  domain: `mixpanel.com`,
  has_workspaces: true,
  id: 42,
  is_demo: false,
  name: `Pro Kitten Trader`,
  organization_id: 33, // not in the 10% of orgs opted into new metrics mode
  type: ProjectType.Normal,
  workspaces: {},
};

export const PROJECT_METADATA: ProjectMetadata = {
  ...MINIMAL_PROJECT_METADATA,

  events_plan_id: 22204,
  external_help_link: [
    {
      link_name: `#mixpanel-data`,
      link_url: `https://mixpanel.slack.com/archives/CM4CHBLLS`,
    },
    {
      link_name: `Tracking reference`,
      link_url: `https://google.com`,
    },
  ],
  feature_flags: [],
  feature_gate_name: `mtu_enterprise`,
  feature_gates: {},
  hasSensitiveAccess: true,
  has_integrated_arb: true,
  has_integrated_engage: true,
  onboarding_dashboard_id: null,
  owner_id: 1,
  people_plan_id: 13928,
  project_as_of_date: null,
  permissions: [`write`],
  role: {
    name: `admin`,
  },
  timezone: `US/Pacific`,
  utc_offset: -420,
  workspaces: {},
};

export const MINIMAL_WORKSPACE: MinimalWorkspace = {
  creator_name: `Steve Cobb`,
  description: `This workspace contains all the data without any filters applied to it`,
  id: 75,
  is_default: false,
  is_global: true,
  name: `Test Global Workspace`,
  project_id: PROJECT_METADATA.id,
};

export const WORKSPACE: Workspace = {
  ...MINIMAL_WORKSPACE,
  created_iso: `2019-10-22T19:28:19.977371`,
  creator_id: 1030710,
  has_all_org_members: false,
  is_restricted: false,
  is_visible: true,
  member_count: 522,
  membership_approval_required: false,
  team_count: 6,
  onboarding_dashboard_id: null,
  data_filters: {
    event_names: {},
    user_properties: {},
    event_properties: {},
  },
  member_list: [],
  team_list: [],
  unified_member_list: [],
};

export const MINIMAL_ALTERNATE_WORKSPACE: MinimalWorkspace = {
  creator_name: `Jun Kim`,
  description: `Non global workspace`,
  id: 79,
  is_default: false,
  is_global: false,
  name: `Test Non Global Workspace`,
  project_id: PROJECT_METADATA.id,
};

export const ALTERNATE_WORKSPACE: Workspace = {
  ...MINIMAL_ALTERNATE_WORKSPACE,
  created_iso: `2019-10-22T19:28:19.977371`,
  creator_id: 1030710,
  has_all_org_members: false,
  is_restricted: false,
  is_visible: true,
  member_count: 10,
  membership_approval_required: false,
  team_count: 6,
  onboarding_dashboard_id: null,
  data_filters: {
    event_names: {},
    user_properties: {},
    event_properties: {},
  },
  member_list: [],
  team_list: [],
  unified_member_list: [],
};

// /api/app/projects/metadata/
export const PROJECT_METADATA_INDEX_RESPONSE: ProjectMetadataIndexResponse = {
  status: `ok`,
  results: {
    [MINIMAL_PROJECT_METADATA.id]: MINIMAL_PROJECT_METADATA,
  },
};

// /api/app/projects/<id>/metadata/
export const PROJECT_METADATA_RESPONSE: ProjectMetadataResponse = {
  status: `ok`,
  results: PROJECT_METADATA,
};

// /api/app/projects/<id>/custom_properties/
export const CUSTOM_PROPERTIES = {
  status: `ok`,
  results: [
    {
      customPropertyId: 1,
      project: 42,
      user: API_USER_DATA.results.user_id,
      name: `No. of Kitten Views`,
      description: ``,
      exampleValue: ``,
      dataGroupId: null,
      resourceType: `people`,
      propertyType: `number`,
      created: `2020-07-14T21:56:55`,
      modified: `2020-07-14T22:03:32`,
      isVisible: true,
      isLocked: false,
      referencedBy: [],
      referencedDirectlyBy: [],
      referencedRawEventProperties: [],
      behavior: {
        event: {
          label: `View kittens`,
          value: `View kittens`,
          custom: false,
        },
        filters: [],
        property: null,
        dateRange: {
          type: `in the last`,
          window: {
            unit: `day`,
            value: 30,
          },
        },
        filtersOperator: `and`,
        aggregationOperator: `total`,
      },
      canUpdateBasic: true,
      can_update_basic: true,
      can_view: true,
      is_superadmin: true,
      can_share: true,
    },
    {
      customPropertyId: 2,
      project: 4,
      user: API_USER_DATA.results.user_id,
      name: `Custom Prop`,
      description: ``,
      exampleValue: ``,
      dataGroupId: null,
      resourceType: `events`,
      propertyType: `string`,
      created: `2020-07-14T22:12:05`,
      modified: `2020-07-14T22:12:05`,
      isVisible: true,
      isLocked: false,
      referencedBy: [],
      referencedDirectlyBy: [],
      referencedRawEventProperties: [`$browser`],
      displayFormula: ` A `,
      composedProperties: {
        A: {
          type: `string`,
          label: `Browser`,
          value: `$browser`,
          resourceType: `event`,
          propertyDefaultType: `string`,
        },
      },
      canUpdateBasic: true,
      can_update_basic: true,
      can_view: true,
      is_superadmin: true,
      can_share: true,
    },
    {
      customPropertyId: 3,
      project: 4,
      user: API_USER_DATA.results.user_id,
      name: `Mapped Custom Prop`,
      description: ``,
      exampleValue: ``,
      dataGroupId: null,
      mappedDataGroupId: `7959329356733535298`,
      joinPropertyType: `string`,
      resourceType: `events`,
      propertyType: `dimension`,
      created: `2020-07-14T22:12:05`,
      modified: `2020-07-14T22:12:05`,
      isVisible: true,
      isLocked: false,
      referencedBy: [],
      referencedDirectlyBy: [],
      referencedRawEventProperties: [`$browser`],
      displayFormula: ` A `,
      composedProperties: {
        A: {
          type: `string`,
          label: `Browser`,
          value: `$browser`,
          resourceType: `event`,
          propertyDefaultType: `string`,
        },
      },
      canUpdateBasic: true,
      can_update_basic: true,
      can_view: true,
      is_superadmin: true,
      can_share: true,
    },
  ],
};

// /api/app/projects/<id>/custom_events/
export const CUSTOM_EVENTS = {
  custom_events: [
    {
      id: 1,
      name: `'murca kittens`,
      alternatives: [{event: `View kittens`, serialized: `properties["mp_country_code"] == "US"`}],
      is_collect_everything_event: false,
      can_view: true,
      can_share: true,
    },
    {
      name: `Autotrack event`,
      alternatives: [
        {
          serialized: `(properties["$event_type"] == "click" or properties["$event_type"] == "change") and (matches_groups(properties["$elements"], {item["attr__href"] == "/posts/create"}))`,
          event: `$web_event`,
        },
      ],
      id: 2,
      is_collect_everything_event: true,
      can_view: true,
      can_share: true,
    },
    {
      name: `Custom event with autotrack event and non autotrack event`,
      alternatives: [
        {
          event: `$custom_event:2`, // autotrack event
          valid_segfilter: {
            filters: [],
            op: `and`,
          },
        },
        {
          event: `View kittens`,
          valid_segfilter: {
            filters: [],
            op: `and`,
          },
        },
      ],
      id: 3,
      is_collect_everything_event: false,
      can_view: true,
      can_share: true,
    },
    {
      name: `Custom event with autotrack event and non autotrack custom event`,
      alternatives: [
        {
          event: `$custom_event:2`, // autotrack event
          valid_segfilter: {
            filters: [],
            op: `and`,
          },
        },
        {
          event: `$custom_event:1`, // non autotrack event
          valid_segfilter: {
            filters: [],
            op: `and`,
          },
        },
      ],
      id: 4,
      is_collect_everything_event: false,
      can_view: true,
      can_share: true,
    },
    {
      name: `Custom event with only autotrack event`,
      alternatives: [
        {
          event: `$custom_event:2`,
          valid_segfilter: {
            filters: [],
            op: `and`,
          },
        },
      ],
      id: 5,
      is_collect_everything_event: false,
      can_view: true,
      can_share: true,
    },
    {
      name: `Custom event with nested autotrack event`,
      alternatives: [
        {
          event: `$custom_event:5`,
          valid_segfilter: {
            filters: [],
            op: `and`,
          },
        },
      ],
      id: 6,
      is_collect_everything_event: false,
      can_view: true,
      can_share: true,
    },
    {
      name: `Custom event with nested nested autotrack event and regular event`,
      alternatives: [
        {
          event: `$custom_event:6`,
          valid_segfilter: {
            filters: [],
            op: `and`,
          },
        },
        {
          event: `View kittens`,
          valid_segfilter: {
            filters: [],
            op: `and`,
          },
        },
      ],
      id: 7,
      is_collect_everything_event: false,
      can_view: true,
      can_share: true,
    },
  ],
};

// /api/app/projects/42/data-definitions/events
export const EVENT_DEFINITIONS = {
  status: `ok`,
  results: [
    {
      apiQueryCount: 27,
      uiQueryCount: 27,
      count: 888,
      description: `This is the most awesome event that happened this year!`,
      id: 123,
      lastModified: `2019-03-18`,
      lastModifiedBy: {name: `Jon Lack`, email: `jon.lack@mixpanel.com`},
      name: `Jon's awesome event`,
      platforms: [`couch`],
      tags: [`human`],
      status: `visible`,
      isMixpanelDefinition: false,
      hidden: false,
    },
    {
      apiQueryCount: 0,
      uiQueryCount: 0,
      count: 88,
      description: `Kittens are now major influencers on Instagram.`,
      id: 4,
      lastModified: `2019-03-18`,
      lastModifiedBy: {name: `Kitty Kat`, email: `kitty.kat@mixpanel.com`},
      name: `View kittens`,
      platforms: [`couch`],
      tags: [`cat`, `animal`],
      status: `hidden`,
      isMixpanelDefinition: true,
      hidden: false,
    },
    {
      apiQueryCount: 777,
      uiQueryCount: 777,
      description: `Puppies are now major influencers on Instagram.`,
      id: 5,
      customEventId: 6,
      lastModified: `2019-03-18`,
      lastModifiedBy: {name: `Puppy Dog`, email: `puppy.dog@mixpanel.com`},
      name: `View puppies`,
      displayName: `PuppyEvent`,
      platforms: [`yard`],
      tags: [`dog`, `animal`],
      alternatives: [{event: `view chihuahua`}],
      status: `visible`,
      hidden: false,
    },
    {
      apiQueryCount: 77,
      uiQueryCount: 77,
      description: `Bears are now major influencers on Instagram.`,
      id: 6,
      customEventId: 7,
      lastModified: `2019-03-18`,
      lastModifiedBy: {name: `Black Bear`, email: `black.bear@mixpanel.com`},
      name: `View bears`,
      displayName: `BearEvent`,
      platforms: [`yard`],
      tags: [`bear`, `animal`],
      alternatives: [{event: `view grizzly`}],
      status: `hidden`,
      hidden: false,
    },
    {
      apiQueryCount: 40,
      uiQueryCount: 40,
      description: `Aardvarks are now major influencers on Instagram.`,
      count: 777,
      id: 124,
      lastModified: `2019-03-18`,
      name: `Feed aardvarks`,
      platforms: [`anthill`],
      tags: [`animal`],
      status: `visible`,
      isMixpanelDefinition: false,
      hidden: false,
    },
    {
      apiQueryCount: 5000,
      uiQueryCount: 5000,
      description: `Alpacas are now major influencers on Instagram.`,
      count: 22,
      id: 7,
      lastModified: `2019-03-18`,
      name: `Shear alpacas`,
      platforms: [`yard`],
      tags: [`animal`],
      status: `hidden`,
      isMixpanelDefinition: true,
      hidden: false,
    },
    {
      apiQueryCount: 789,
      uiQueryCount: 789,
      description: `Emus are now major influencers on Instagram.`,
      id: 8,
      customEventId: 10,
      lastModified: `2019-03-18`,
      name: `Ride emu`,
      displayName: `EmuEvent`,
      platforms: [`yard`],
      tags: [`bird`, `animal`],
      alternatives: [{event: `view ostrich`}],
      status: `visible`,
      hidden: true,
    },
    {
      apiQueryCount: 10,
      uiQueryCount: 10,
      description: `Tapirs are now major influencers on Instagram.`,
      id: 9,
      customEventId: 11,
      lastModified: `2019-03-18`,
      name: `Hug tapir`,
      displayName: `TapirEvent`,
      platforms: [`forest`],
      tags: [`animal`],
      alternatives: [{event: `view boar`}],
      status: `hidden`,
      hidden: false,
    },
  ],
};

// /api/app/projects/42/data-definitions/properties?resourceType=Event
export const EVENT_PROPERTY_DEFINITIONS = {
  status: `ok`,
  results: [
    {
      apiQueryCount: 77,
      uiQueryCount: 77,
      count: 1683406,
      description: `Name of kitten`,
      id: 1095,
      name: `Kitten name`,
      displayName: null,
      resourceType: `event`,
      type: `string`,
      exampleValue: `Tom`,
      status: `visible`,
      isMixpanelDefinition: true,
    },
    {
      apiQueryCount: 777,
      uiQueryCount: 777,
      count: 1883406,
      description: `All of the gorgeous little kittens`,
      id: 1096,
      name: `Number of kittens`,
      displayName: null,
      resourceType: `event`,
      type: `number`,
      exampleValue: `999999`,
      status: `hidden`,
      isMixpanelDefinition: false,
    },
    {
      apiQueryCount: 88,
      uiQueryCount: 88,
      count: 1683406,
      description: `Name of puppy`,
      id: 1097,
      name: `Puppy name`,
      displayName: `Good boi name`,
      resourceType: `event`,
      type: `string`,
      exampleValue: `Luna`,
      status: `visible`,
      isMixpanelDefinition: true,
    },
    {
      apiQueryCount: 888,
      uiQueryCount: 888,
      count: 1883406,
      description: `All of the gorgeous little puppies`,
      id: 1098,
      name: `Number of puppies`,
      displayName: `# good bois`,
      resourceType: `event`,
      type: `number`,
      exampleValue: `999999`,
      status: `hidden`,
      isMixpanelDefinition: false,
    },
    {
      apiQueryCount: 434,
      uiQueryCount: 434,
      count: 23523,
      dimension_data_group_id: `7959329356733535298`,
      description: `Species of kitten`,
      id: 7323,
      name: `Species`,
      displayName: ``,
      resourceType: `event`,
      type: `dimension`,
      exampleValue: `Bengal Tiger`,
      status: `live`,
      dropped: false,
      merged: false,
      hasArbData: true,
      sensitive: false,
      join_property_type: `string`,
    },
    {
      apiQueryCount: 309,
      uiQueryCount: 309,
      count: 8675309,
      description: `Cuteness level`,
      id: 1098,
      name: `Cuteness level`,
      displayName: `Cuteness level`,
      resourceType: `event`,
      type: `number`,
      exampleValue: `999999`,
      hidden: true,
      status: `hidden`,
      isMixpanelDefinition: false,
    },
  ],
};

// /api/app/projects/42/data-definitions/properties?resourceType=User
export const USER_PROPERTY_DEFINITIONS = {
  status: `ok`,
  results: [
    {
      apiQueryCount: 77,
      uiQueryCount: 77,
      count: 1883406,
      description: `Name of puppy`,
      id: 1095,
      name: `puppy name`,
      displayName: null,
      resourceType: `event`,
      type: `string`,
      exampleValue: `Fido`,
      status: `visible`,
      isMixpanelDefinition: false,
    },
    {
      apiQueryCount: 777,
      uiQueryCount: 777,
      count: 1883406,
      description: `All of the gorgeous little puppies`,
      id: 1096,
      name: `Number of puppies`,
      displayName: null,
      resourceType: `event`,
      type: `number`,
      exampleValue: `999999`,
      status: `hidden`,
      isMixpanelDefinition: true,
    },
    {
      apiQueryCount: 88,
      uiQueryCount: 88,
      count: 1883406,
      description: `Name of aardvark`,
      id: 1097,
      name: `aardvark name`,
      displayName: `Aardvark's name`,
      resourceType: `event`,
      type: `string`,
      exampleValue: `Arthur`,
      status: `visible`,
      isMixpanelDefinition: false,
    },
    {
      apiQueryCount: 888,
      uiQueryCount: 888,
      count: 1883406,
      description: `All of the gorgeous little aardvarks`,
      id: 1098,
      name: `Number of aardvarks`,
      displayName: `# aardvarks`,
      resourceType: `event`,
      type: `number`,
      exampleValue: `999999`,
      status: `hidden`,
      isMixpanelDefinition: true,
    },
    {
      apiQueryCount: 7465,
      uiQueryCount: 7465,
      count: 9947857,
      description: `The best food`,
      id: 1099,
      name: `Favorite food`,
      displayName: null,
      resourceType: `event`,
      type: `string`,
      exampleValue: `Steak`,
      status: `hidden`,
      isMixpanelDefinition: false,
      hidden: true,
    },
  ],
};

// /api/2.0/engage/properties
export const ENGAGE_PROPS = {
  status: `ok`,
  results: {
    $created: {
      count: 4312,
      type: `datetime`,
    },
    'number of kittens owned': {
      count: 4310,
      type: `number`,
    },
  },
};

// /api/2.0/engage/stats
export const ENGAGE_STATS = {
  status: `ok`,
  results: 10,
};

// /api/2.0/engage/properties?data_group_id=:id
export const DATA_GROUP_ENGAGE_PROPS = {
  status: `ok`,
  results: {
    'Account Status': {
      count: 4310,
      type: `string`,
    },
  },
};
export const DATA_GROUP_ENGAGE_PROPS_COMPANY = {
  status: `ok`,
  results: {
    ARR: {
      count: 2345,
      type: `number`,
    },
  },
};

// /api/2.0/events/names/
export const EVENTS_NAMES = [`Jon's awesome event`, `View kittens`, `$journey_entered`, `$signup`];

export const ALL_EVENTS_NAMES = [
  ...EVENTS_NAMES.map((eventName) => getOwnAttr(EVENTS, eventName) || eventName),
  ...CUSTOM_EVENTS.custom_events.map((customEvent) => customEvent.name),
].sort();

// /api/2.0/events/properties/toptypes/
export const COMMON_TOP_TYPES = {
  $browser: {
    count: 1883414,
    type: `string`,
  },
  $referrer: {
    count: 1315302,
    type: `string`,
  },
  'Number of kittens': {
    count: 1883406,
    type: `number`,
  },
  'Kitty types': {
    count: 1883406,
    type: `list`,
  },
  Species: {
    count: 23523,
    type: `dimension`,
    dimension_data_group_id: `7959329356733535298`,
    join_property_type: `string`,
  },
};

export const LOOKUP_TABLE_TOP_TYPES = {
  size: {count: 1200, type: `string`},
  personality: {count: 1200, type: `string`},
};

export const PROPS_TOPTYPES = (params) => {
  const dataGroupId = params.data_group_id;
  const events = params.event && JSON.parse(params.event);

  if (dataGroupId) {
    return LOOKUP_TABLE_TOP_TYPES;
  } else if (Array.isArray(events) && events.includes(`View kittens`)) {
    return Object.assign({'Kitten Name': {count: 1683406, type: `string`}}, COMMON_TOP_TYPES);
  } else {
    return COMMON_TOP_TYPES;
  }
};

// /api/2.0/events/properties/values
export const PROPS_VALUES = [
  // numeric "Number of kittens" values
  `1500`,
  `29`,
  `483`,
  `22348`,
  `67`,
  `32`,
];

// /api/app/projects/:id/cohorts/
export const COHORTS = {
  results: [
    {
      id: 1,
      name: `Cohort One`,
      description: `test description`,
      created_by: {name: `Jordan Nuñez`, email: `jordan@mixpanel.com`, id: 12345},
      last_queried: `2017-07-25`,
      count: 42,
      can_view: true,
      can_update_basic: true,
      selector: {
        children: [
          {
            children: [
              {property: `predefined_cohort`, value: `$all_users`},
              {property: `literal`, value: false},
            ],
            operator: `or`,
          },
          {property: `literal`, value: true},
        ],
        operator: `and`,
      },
    },
    {
      id: 2,
      name: `Cohort Two`,
      description: `who queried chrome`,
      last_queried: `2017-07-20`,
      created_by: {name: `Chi Zhang`, email: `chi@mixpanel.com`, id: 54321},
      count: 252034,
      can_view: true,
    },
    {
      id: 3,
      name: `Hidden cohort`,
      description: `this cohort should not be displayed`,
      created_by: {name: `Chi Zhang`, email: `chi@mixpanel.com`, id: 54321},
      last_queried: `2017-07-25`,
      count: 42,
      can_view: false,
    },
  ],
};

export const HIDDEN_COHORTS = {
  results: [
    {
      id: 1,
      name: `Cohort One`,
      description: `test description`,
      created_by: {name: `Jordan Nuñez`, email: `jordan@mixpanel.com`, id: 12345},
      last_queried: `2017-07-25`,
      count: 42,
      can_view: false,
    },
    {
      id: 2,
      name: `Cohort Two`,
      description: `who queried chrome`,
      last_queried: `2017-07-20`,
      created_by: {name: `Chi Zhang`, email: `chi@mixpanel.com`, id: 54321},
      count: 252034,
      can_view: false,
    },
  ],
};

export const B2B_COHORTS = {
  results: [
    {
      id: 6,
      name: `B2B Cohort One`,
      description: `test description`,
      created_by: {name: `J`, email: `j.connolly@mixpanel.com`, id: 123456},
      last_queried: `2019-12-18`,
      count: 42,
      can_view: true,
      data_group_id: BILLING_DATA_GROUP_ID,
    },
    {
      id: 7,
      name: `B2B Cohort Two`,
      description: `who queried chrome`,
      last_queried: `2019-12-17`,
      created_by: {name: `Chi Zhang`, email: `chi@mixpanel.com`, id: 54321},
      count: 252034,
      can_view: true,
      data_group_id: BILLING_DATA_GROUP_ID,
    },
  ],
};

// /api/app/projects/:id/experiments/
export const EXPERIMENTS = {
  results: [],
};

// /api/app/projects/:id/messages/variants/
export const MESSAGE_VARIANTS = {
  results: [
    {
      variants: [1, 2],
      name: `Test Message`,
    },
  ],
};

// /api/app/projects/:id/data-groups/
export const DATA_GROUPS = {
  results: [
    {
      data_group_id: `9292783`,
      display_name: `Billing Account`,
      property_name: `billing_account`,
      is_ready: true,
    },
    {
      data_group_id: `2900955`,
      display_name: `Company`,
      property_name: `Company`,
      is_ready: true,
    },
  ],
};

// api/app/projects/:id/data-definitions/lookup-tables
export const LOOKUP_TABLES = {
  status: `ok`,
  results: [
    {
      createdAt: `2020-06-02`,
      id: `7959329356733535298`,
      lastModifiedAt: `2020-06-02`,
      name: `project_cluster_mapping.csv`,
    },
  ],
};

export const AVATARS = {
  base: `https://avatars-base-url`,
  avatars: {
    'id-1': `avatar-1`,
    'id-2': `avatar-2`,
  },
};

// /api/app/projects/:id/features
export function makeFeaturesResponse({
  projectFeatureFlags = [],
  userFeatureFlags = [],
  featureGates = {},
} = {}): SuccessResponse<GetFeaturesResults> {
  return {
    status: `ok`,
    results: {
      user_feature_flags: userFeatureFlags,
      project_feature_flags: projectFeatureFlags,
      project_feature_gate_name: `mtu_enterprise`,
      project_feature_gates: {
        allow_custom_alerts: true,
        allow_smart_hub: true,
        allow_raw_data_export: true,
        allow_formatted_export: true,
        can_export_csv: true,
        can_hide_events_and_properties: true,
        can_drop_events_and_properties: true,
        can_merge_events_and_properties: true,
        can_segment_retention: true,
        can_use_platform_github: true,
        max_custom_apps: 15,
        max_custom_events: Number.MAX_SAFE_INTEGER,
        max_dashboard_digests: Number.MAX_SAFE_INTEGER,
        max_data_history_days: 1825,
        max_insights_filters: Number.MAX_SAFE_INTEGER,
        max_org_members: Number.MAX_SAFE_INTEGER,
        max_projects: Number.MAX_SAFE_INTEGER,
        max_project_members: Number.MAX_SAFE_INTEGER,
        max_saved_reports: Number.MAX_SAFE_INTEGER,
        max_segmentation_filters: Number.MAX_SAFE_INTEGER,
        restricted_reports: [],
        restrict_entity_saving: [],
        sso: true,
        unlimited: Number.MAX_SAFE_INTEGER,
        can_use_datawarehouse_export: true,
        b2b_analytics: true,
        workspaces: true,
        b2b_analytics_groups: 3,
        ...featureGates,
      },
    },
  };
}
